import type { SagaIterator } from 'redux-saga';
import { put, select, takeEvery } from 'redux-saga/effects';
import i18n from '../i18n';
import { navigate } from '../routes/router';
import {
  DEFAULT_LANGUAGE,
  isLanguageCode,
  LANGUAGE_COOKIE,
  LANGUAGE_COOKIE_DOMAIN,
  LANGUAGE_COOKIE_MAX_AGE,
  type LanguageCode,
} from '../shared/languages';
import { getCookie, setCookie } from '../utils/utils';
import { SAGA_ACTIONS } from './sagaActions';

type RouteParams = Record<string, string | number> | null | undefined;

function withLanguage(
  params: RouteParams,
  language: LanguageCode,
): Record<string, string | number> {
  const { language: _current, ...rest } = params ?? {};
  return language === DEFAULT_LANGUAGE ? rest : { ...rest, language };
}

function currentLanguageParam(params: RouteParams): string | undefined {
  return typeof params?.language === 'string' ? params.language : undefined;
}

export function* applyInitialLanguage(): SagaIterator<void> {
  const routing = yield select((state) => state.routing);

  const urlLanguage = currentLanguageParam(routing.params);
  const cookieLanguage = getCookie(LANGUAGE_COOKIE);

  const resolved: LanguageCode = isLanguageCode(urlLanguage)
    ? urlLanguage
    : isLanguageCode(cookieLanguage)
      ? cookieLanguage
      : DEFAULT_LANGUAGE;

  i18n.changeLanguage(resolved);
  setCookie(
    LANGUAGE_COOKIE,
    resolved,
    LANGUAGE_COOKIE_DOMAIN,
    LANGUAGE_COOKIE_MAX_AGE,
  );

  // No route matched (e.g. a typo'd path) — nothing to redirect to.
  if (routing.id === 'NOT_FOUND') {
    return;
  }

  const canonicalLanguage =
    resolved === DEFAULT_LANGUAGE ? undefined : resolved;
  if (urlLanguage === canonicalLanguage) {
    return;
  }

  yield put(
    navigate(routing.id, withLanguage(routing.params, resolved), {
      query: routing.query ?? undefined,
      replace: true,
    }),
  );
}

function* setLanguage(action: {
  type: string;
  language: string;
}): SagaIterator<void> {
  const { language } = action;
  if (!isLanguageCode(language)) {
    return;
  }

  const routing = yield select((state) => state.routing);
  const canonicalLanguage =
    language === DEFAULT_LANGUAGE ? undefined : language;
  if (currentLanguageParam(routing.params) === canonicalLanguage) {
    return;
  }

  setCookie(
    LANGUAGE_COOKIE,
    language,
    LANGUAGE_COOKIE_DOMAIN,
    LANGUAGE_COOKIE_MAX_AGE,
  );
  i18n.changeLanguage(language);

  if (routing.id === 'NOT_FOUND') {
    return;
  }

  yield put(
    navigate(routing.id, withLanguage(routing.params, language), {
      query: routing.query ?? undefined,
    }),
  );
}

export function* watchSetLanguage(): SagaIterator<void> {
  yield takeEvery(SAGA_ACTIONS.SET_LANGUAGE, setLanguage);
}
